import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Separator,
  Skeleton,
  Text,
} from "@musa.tech/design-system";
import { Download, FileArchive, Loader2, AlertTriangle } from "lucide-react";
import { useMtrBulkDownloadSummary } from "@/hooks/useMtrBulkDownloadSummary";
import { useMtrBulkDownload } from "@/hooks/useMtrBulkDownload";
import { useCdfBulkDownloadSummary } from "@/hooks/useCdfBulkDownloadSummary";
import { useCdfBulkDownload } from "@/hooks/useCdfBulkDownload";
import { MtrBatchGrid } from "@/components/collects/MtrBatchGrid";
import { useDemoMode } from "@/contexts/DemoModeContext";
import type { MtrBulkScope } from "@/hooks/useCollectsApiData";
import type { FilterState } from "@/hooks/useFilters";

type DocumentType = "mtr" | "cdf";

interface BaixarMtrsModalProps {
  filters: FilterState;
  /** Escopo dos MTRs baixados: 'historico' (realizadas) ou 'proximas' (planejadas). */
  scope?: MtrBulkScope;
  /**
   * Habilita a seleção MTR/CDF. Só o Histórico usa: coleta planejada não tem MTR
   * recebido, logo nunca tem CDF.
   */
  allowCdf?: boolean;
}

/** Formata "YYYY-MM-DD" → "DD/MM/YYYY" sem depender de fuso. */
const formatDate = (value: string): string => {
  const [year, month, day] = (value || "").slice(0, 10).split("-");
  return day ? `${day}/${month}/${year}` : value;
};

export const BaixarMtrsModal = ({
  filters,
  scope = "historico",
  allowCdf = false,
}: BaixarMtrsModalProps) => {
  const { isDemoMode } = useDemoMode();
  const [open, setOpen] = useState(false);
  const [documentType, setDocumentType] = useState<DocumentType>("mtr");

  const isCdf = allowCdf && documentType === "cdf";
  const label = allowCdf ? "Baixar MTR/CDF" : "Baixar MTRs";

  const mtrSummary = useMtrBulkDownloadSummary(filters, open && !isCdf, scope);
  const cdfSummary = useCdfBulkDownloadSummary(filters, open && isCdf, scope);

  const {
    data: summary,
    isLoading,
    isError,
    refetch,
  } = isCdf ? cdfSummary : mtrSummary;

  const mtrDownload = useMtrBulkDownload(
    filters,
    scope,
    mtrSummary.data?.total_mtrs,
    mtrSummary.data?.batch_size,
  );
  const cdfDownload = useCdfBulkDownload(filters, scope);

  const { isDownloading, activeBatch, downloadedBatches, downloadBatch, resetProgress } =
    isCdf ? cdfDownload : mtrDownload;

  // Zera o progresso de lotes sempre que o modal é aberto (não persiste).
  useEffect(() => {
    if (open) {
      setDocumentType("mtr");
      mtrDownload.resetProgress();
      cdfDownload.resetProgress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (isDemoMode) return null;

  const totalBatches = summary?.total_batches ?? 0;
  const totalFiles = isCdf
    ? (summary as { total_cdfs?: number } | undefined)?.total_cdfs ?? 0
    : (summary as { total_mtrs?: number } | undefined)?.total_mtrs ?? 0;
  const totalWithDocument = isCdf
    ? (summary as { total_collects_with_cdf?: number } | undefined)
        ?.total_collects_with_cdf ?? 0
    : (summary as { total_collects_with_mtr?: number } | undefined)
        ?.total_collects_with_mtr ?? 0;
  const documentLabel = isCdf ? "CDF" : "MTR";
  const hasMultipleBatches = totalBatches > 1;

  const selectDocumentType = (next: DocumentType) => {
    if (next === documentType) return;
    setDocumentType(next);
    resetProgress();
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        aria-label={`${label} em massa`}
      >
        <FileArchive className="w-4 h-4 mr-2 flex-shrink-0" />
        <span className="truncate">{label}</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[95vw] sm:max-w-[560px] bg-white rounded-lg">
          <DialogHeader className="border-b border-neutral-200 pb-3">
            <DialogTitle>
              <Text variant="heading" size="large" className="text-neutral-900">
                {label}
              </Text>
            </DialogTitle>
          </DialogHeader>

          {allowCdf && (
            <div
              role="tablist"
              aria-label="Tipo de documento"
              className="flex gap-1 rounded-md bg-neutral-100 p-1"
            >
              {(["mtr", "cdf"] as DocumentType[]).map((type) => (
                <button
                  key={type}
                  type="button"
                  role="tab"
                  aria-selected={documentType === type}
                  data-testid={`baixar-massa-tab-${type}`}
                  onClick={() => selectDocumentType(type)}
                  className={`flex-1 rounded px-3 py-1.5 text-sm font-medium transition-colors ${
                    documentType === type
                      ? "bg-white text-neutral-900 shadow-sm"
                      : "text-neutral-600 hover:text-neutral-900"
                  }`}
                >
                  {type.toUpperCase()}
                </button>
              ))}
            </div>
          )}

          <div className="py-2 space-y-4">
            {isLoading && (
              <div className="space-y-3">
                <Skeleton className="h-5 w-2/3" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-24 w-full rounded-md" />
              </div>
            )}

            {!isLoading && isError && (
              <div className="flex flex-col items-center gap-3 py-6 text-center">
                <AlertTriangle className="h-8 w-8 text-error-500" />
                <Text variant="body" className="text-neutral-700">
                  Não foi possível carregar o resumo. Tente novamente.
                </Text>
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  Tentar novamente
                </Button>
              </div>
            )}

            {!isLoading && !isError && summary && totalFiles === 0 && (
              <div className="flex flex-col items-center gap-2 py-6 text-center">
                <FileArchive className="h-8 w-8 text-neutral-400" />
                <Text variant="body" className="text-neutral-700">
                  Nenhum {documentLabel} encontrado para os filtros selecionados.
                </Text>
              </div>
            )}

            {!isLoading && !isError && summary && totalFiles > 0 && (
              <>
                <div className="space-y-1">
                  <Text variant="body" className="text-neutral-900">
                    <span className="font-semibold">{totalFiles}</span>{" "}
                    {documentLabel}
                    {totalFiles > 1 ? "s" : ""} serão baixados.
                  </Text>
                  <Text variant="label" size="small" className="text-neutral-500">
                    {totalWithDocument} de {summary.total_collects} coleta
                    {summary.total_collects > 1 ? "s" : ""} possuem{" "}
                    {documentLabel} nos filtros aplicados.
                  </Text>
                </div>

                {hasMultipleBatches && (
                  <div className="flex items-start gap-2 rounded-md bg-secondary-100 p-3">
                    <AlertTriangle className="h-4 w-4 text-warning-500 mt-0.5 flex-shrink-0" />
                    <Text variant="label" size="small" className="text-neutral-700">
                      O download é limitado a {summary.batch_size}{" "}
                      {documentLabel}s por vez. Baixe os {totalBatches} lotes
                      abaixo para obter todos os arquivos.
                    </Text>
                  </div>
                )}

                {/* Prévia dos primeiros arquivos */}
                {summary.preview.length > 0 && (
                  <div className="rounded-md border border-neutral-200">
                    <div className="px-3 py-2 bg-neutral-100">
                      <Text variant="label" size="small" className="text-neutral-600">
                        Prévia
                      </Text>
                    </div>
                    <div className="divide-y divide-neutral-100 max-h-40 overflow-y-auto">
                      {isCdf
                        ? cdfSummary.data?.preview.map((item, idx) => (
                            <div
                              key={`${item.certificate_id}-${idx}`}
                              className="px-3 py-2 flex items-center justify-between gap-3"
                            >
                              <div className="min-w-0">
                                <Text
                                  variant="body"
                                  size="small"
                                  className="text-neutral-800 truncate"
                                >
                                  CDF {item.cdf_number}
                                </Text>
                                <Text
                                  variant="label"
                                  size="small"
                                  className="text-neutral-500 truncate"
                                >
                                  {item.issuer}
                                  {item.end_date
                                    ? ` · ${formatDate(item.end_date)}`
                                    : ""}{" "}
                                  · {item.collects_count} coleta
                                  {item.collects_count > 1 ? "s" : ""}
                                </Text>
                              </div>
                            </div>
                          ))
                        : mtrSummary.data?.preview.map((item, idx) => (
                            <div
                              key={`${item.collect_id}-${item.mtr_number}-${idx}`}
                              className="px-3 py-2 flex items-center justify-between gap-3"
                            >
                              <div className="min-w-0">
                                <Text
                                  variant="body"
                                  size="small"
                                  className="text-neutral-800 truncate"
                                >
                                  {item.establishment_name ?? "—"}
                                </Text>
                                <Text
                                  variant="label"
                                  size="small"
                                  className="text-neutral-500 truncate"
                                >
                                  {formatDate(item.planned_date)} · MTR{" "}
                                  {item.mtr_number}
                                  {item.material_name
                                    ? ` · ${item.material_name}`
                                    : ""}
                                </Text>
                              </div>
                            </div>
                          ))}
                    </div>
                  </div>
                )}

                {/* Ação de download */}
                {hasMultipleBatches ? (
                  <MtrBatchGrid
                    totalBatches={totalBatches}
                    batchSize={summary.batch_size}
                    documentLabel={documentLabel}
                    downloadedBatches={downloadedBatches}
                    activeBatch={activeBatch}
                    isDownloading={isDownloading}
                    onDownloadBatch={downloadBatch}
                  />
                ) : (
                  <Button
                    variant="default"
                    className="w-full"
                    disabled={isDownloading}
                    onClick={() => downloadBatch(0)}
                  >
                    {isDownloading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    {isDownloading ? "Gerando arquivo..." : "Baixar arquivo (.zip)"}
                  </Button>
                )}
              </>
            )}
          </div>

          <Separator />
          <DialogFooter className="pt-3">
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BaixarMtrsModal;
